import {
  createBucket,
  createRemoteBucketClient,
  createSyncedBucket,
  type BucketDriver,
  type ClientBucketMetadataDb,
  type SyncedBucket,
} from '@w/bucket'
import { createTursoWorkerDriver } from '@w/db-turso-browser-adapter/worker'
import { UFile, UFileStream } from '@w/utils'
import {
  createBucketBrowserDriver,
  createTursoClientBucketMetadataDb,
} from '@w/bucket-browser-driver'
import { FLASHCARDS_MEDIA_BUCKET_NAME } from '../server/media/flashcards-media-be'
import type { Tel } from '@w/tel'
import { loadImage, type ImageLoader } from '@w/ui/components/load-image'
import { createMediaPreparation, type PreparedMedia } from './media-preparation'

// flashcards-local-first.spec.md PrincipalFetch: the app passes its one boundary as `fetch`.
type FlashcardsMediaConnection = { baseUrl?: string; fetch?: Window['fetch'] }

export type CreateFlashcardsMediaClientOptions = FlashcardsMediaConnection & {
  principal: string
  isActive?: () => boolean
  /** false for the anonymous principal: a local-only client, with no pull or push (flashcards-local-first.spec.md). */
  remote?: boolean
  driver?: BucketDriver
  metadataDb?: ClientBucketMetadataDb | ((bucketName: string) => ClientBucketMetadataDb)
  imageLoader?: ImageLoader
  logging?: boolean
  tel: Tel
}

export type FlashcardsMediaClient = SyncedBucket & {
  readonly principal: string
  close(): Promise<void>
  getUrl(path: string): string
  subscribeMediaWrites(onWrite: (path: string) => void): () => void
  readonly sessionId: string
  prepare(paths: readonly string[]): Promise<PreparedMedia[]>
}

/** Composition-root factory only; returned handles cannot select another principal. */
export function createFlashcardsMediaFactory(options: FlashcardsMediaConnection & Omit<CreateFlashcardsMediaClientOptions, 'principal' | 'isActive'>) {
  const driver = options.driver ?? createBucketBrowserDriver({ cacheStorage: globalThis.caches })
  const metadataByName = new Map<string, ClientBucketMetadataDb>()
  let injectedOwner: string | undefined
  return (principal: string, isActive: () => boolean) => {
    const injected = options.metadataDb
    if (injected && typeof injected !== 'function') {
      if (injectedOwner !== undefined && injectedOwner !== principal) throw new Error('Principal switch requires a metadata factory')
      injectedOwner = principal
    }
    const factory = typeof injected === 'function' ? (name: string) => {
      const db = metadataByName.get(name) ?? injected(name)
      metadataByName.set(name, db)
      return db
    } : injected
    return createFlashcardsMediaClient({ ...options, driver, metadataDb: factory, principal, isActive })
  }
}

export function createFlashcardsMediaClient(
  options: CreateFlashcardsMediaClientOptions,
): FlashcardsMediaClient {
  const principal = options.principal
  const tel = options.tel
  const namespace = `flashcards-media-v2/${encodeURIComponent(principal)}/`
  let closed = false
  function active() {
    if (closed || options.isActive?.() === false) {
      void close()
      throw new DOMException('Media session closed', 'AbortError')
    }
  }
  // Guard every asynchronous storage/transport completion, including background pull.
  function guarded<T extends object>(target: T): T {
    return new Proxy(target, {
      get(object, key, receiver) {
        const value: unknown = Reflect.get(object, key, receiver)
        if (typeof value !== 'function') return value
        return (...args: unknown[]) => {
          active()
          const result: unknown = Reflect.apply(value, object, args)
          return Promise.resolve(result).then(value => { active(); return value })
        }
      },
    })
  }
  const rawDriver = options.driver ?? createBucketBrowserDriver({ cacheStorage: globalThis.caches })
  const driver: BucketDriver = {
    __bucket__: { logging: options.logging ?? false },
    put(file) {
      active()
      const stored = file instanceof UFile
        ? new UFile(file.blob, { path: namespace + file.path, meta: file.meta })
        : new UFileStream({ path: namespace + file.path, size: file.size, type: file.type, meta: file.info.meta }, file.stream)
      return rawDriver.put(stored)
    },
    async get(path) {
      const file = await rawDriver.get(namespace + path)
      return file && new UFile(file.blob, { path, meta: file.meta })
    },
    delete(paths) { return rawDriver.delete((Array.isArray(paths) ? paths : [paths]).map(path => namespace + path)) },
    move(from, to) { return rawDriver.move(namespace + from, namespace + to) },
    async list() { return (await rawDriver.list()).filter(path => path.startsWith(namespace)).map(path => path.slice(namespace.length)) },
    async _clear() { await rawDriver.delete((await rawDriver.list()).filter(path => path.startsWith(namespace))) },
  }
  const bucketName = JSON.stringify([FLASHCARDS_MEDIA_BUCKET_NAME, principal])
  let metadataDriver: ReturnType<typeof createTursoWorkerDriver> | undefined
  const metadataDb = typeof options.metadataDb === 'function' ? options.metadataDb(bucketName)
    : options.metadataDb ?? createTursoClientBucketMetadataDb({ bucketName, tel, makeDriver: () => {
      active()
      metadataDriver = createTursoWorkerDriver({ path: `bucket-meta-${encodeURIComponent(bucketName)}.db`, tel })
      return metadataDriver
    } })
  const base = options.baseUrl?.replace(/\/$/u, '') ?? ''
  const transport = options.fetch ?? globalThis.fetch
  const remoteBucket = createRemoteBucketClient({ bucketName: FLASHCARDS_MEDIA_BUCKET_NAME, tel,
    fetch: async (input, init) => {
      active()
      const response = await transport(base + (input instanceof Request ? input.url : input.toString()), init)
      active()
      return response
    },
  })
  const syncedBucket = createSyncedBucket({ tel,
    bucket: createBucket({ name: bucketName, logging: options.logging, driver: guarded(driver) }),
    metadataDb: guarded(metadataDb), remoteBucket: options.remote === false ? undefined : guarded(remoteBucket), logging: options.logging,
  })
  const localUrls = new Map<string, string>()
  const originalPut = syncedBucket.put.bind(syncedBucket)
  const originalGet = syncedBucket.get.bind(syncedBucket)

  let closing: Promise<void> | undefined
  function close() {
    if (closing) return closing
    closed = true
    for (const url of localUrls.values()) URL.revokeObjectURL(url)
    localUrls.clear()
    return closing = metadataDriver?.close?.() ?? Promise.resolve()
  }
  const client = Object.assign(syncedBucket, {
    principal, close,
    async put(file: Parameters<SyncedBucket['put']>[0]) {
      active()
      const result = await originalPut(file)
      active()
      if (file instanceof UFile && !localUrls.has(file.path)) {
        localUrls.set(file.path, file.createObjectURL())
      }
      preparation.written(file.path)
      return result
    },
    async get(path: string) {
      active()
      const file = await originalGet(path, { retry: false })
      active()
      // Reuse the object URL. Revoking and recreating it on every get made the
      // currently-rendered <img> src invalid for an instant, so the picture
      // blanked and reloaded on every button press in the trainer. The bytes
      // behind a path never change — media is content-addressed — so one URL
      // per path is correct and stable.
      if (file && !localUrls.has(path)) {
        localUrls.set(path, file.createObjectURL())
      }
      return file
    },
    getUrl(path: string) {
      active()
      assertStoredMediaPath(path)
      // Rendering always uses the current principal's retained bytes. Reopening
      // the cache prepares a new local URL; a CDN address would require a network
      // request again and could bypass the account-bound cache admission.
      return localUrls.get(path) ?? ''
    },
  })
  // Synchronous URL lookup and close stay synchronous; SyncedBucket operations are async.
  const preparation = createMediaPreparation({
    active,
    get: path => client.get(path),
    getUrl: path => client.getUrl(path),
    imageLoader: options.imageLoader ?? loadImage,
  })
  const preparedClient = Object.assign(client, preparation)
  return new Proxy(preparedClient, {
    get(object, key, receiver) {
      const value: unknown = Reflect.get(object, key, receiver)
      if (typeof value !== 'function' || key === 'close') return value
      return (...args: unknown[]) => { active(); const result: unknown = Reflect.apply(value, object, args); return result }
    },
  })
}

function assertStoredMediaPath(value: string): void {
  if (/^(?:[a-z][a-z0-9+.-]*:|\/\/)/iu.test(value)) {
    throw new Error(`media id/path expected, got URL: ${value}`)
  }
}
